import { CascadeMenu } from '@aibees/elephant/components/cascade-menu';
import { SMART_LEAD_CAMPAIGN_ANALYTICS_URL } from '@aibees/elephant/constants/integration';
import { useExportFileDownload } from '@aibees/elephant/feature/exports-management-table/actions/export-file-action';
import { useRetryIntegrationApi } from '@aibees/elephant/feature/exports-management-table/api/use-retry-integration-api';
import { EditExportDialog } from '@aibees/elephant/feature/exports-management-table/components/edit-export-dialog';
import {
  ExportIntegrationStatus,
  ExportSearchGrouping,
} from '@aibees/elephant/hooks';
import { ROUTES as R } from '@aibees/elephant/router';
import { notificationIconMap } from '@aibees/elephant/utils';
import { PopoverListButtonProps, usePopover } from '@aibees/shared/components';
import {
  useBoolean,
  useCopyToClipboard,
  useNotify,
} from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils/class-names';
import { random } from 'lodash-es';
import { memo } from 'react';
import { getExportActionMenuItemsOptions } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import {
  type ExportTableActionProps as Props,
  ExportTableActionType as Action,
} from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

// The "Filter by Export" sub-menu opens to the RIGHT of the clicked row, like a
// nested submenu (anchored top-right of the item, growing rightwards).
const CASCADE_ANCHOR_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};
const CASCADE_TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'left' as const,
};

// Same items/colors as the Lists & Labels row cascades (see
// list-search-table-action) so the menu looks identical across tabs.
const CASCADE_ITEMS = [
  {
    key: 'include',
    label: 'Include',
    icon: 'add-circle-outlined' as const,
    iconColor: 'primary.500',
  },
  {
    key: 'exclude',
    label: 'Exclude',
    icon: 'stop-sign' as const,
    iconColor: 'error.500',
  },
  {
    key: 'include_and',
    label: 'Include (AND)',
    icon: 'add-task' as const,
    iconColor: 'warning.500',
  },
];

const URL_KEY: Record<string, { company: string; people: string }> = {
  include: {
    company: 'companies_exports_request_include',
    people: 'people_exports_request_include',
  },
  exclude: {
    company: 'companies_exports_request_exclude',
    people: 'people_exports_request_exclude',
  },
  include_and: {
    company: 'companies_exports_request_all_of',
    people: 'people_exports_request_all_of',
  },
};

export const ExportTableAction = memo(({ className, item }: Props) => {
  const notify = useNotify();
  const clip = useCopyToClipboard();
  const editSearchState = useBoolean(false);
  const integrationExportFailedDialogState = useBoolean(false);
  const retryIntegrationApi = useRetryIntegrationApi(item.id);
  const { downloadFile } = useExportFileDownload(item);

  // The ⋯ menu is controlled so it can stay open while the "Filter by Export"
  // sub-menu (cascadePopover) is open beside it, and be closed programmatically.
  const moreMenuPopover = usePopover();
  const cascadePopover = usePopover();

  const handleCascadeSelect = (parentKey: string) => {
    moreMenuPopover.onClose();
    const subKey =
      item.grouping === ExportSearchGrouping.COMPANY ? 'company' : 'people';
    const urlKey = URL_KEY[parentKey]?.[subKey];
    if (!urlKey) return;
    const route = subKey === 'company' ? R.searchCompany : R.searchPeople;
    const value = encodeURIComponent(
      `${item.name}::${encodeURIComponent(item.id)}`
    );
    window.open(`${route}?${urlKey}=${value}`, '_blank');
  };

  const handleSelect: PopoverListButtonProps['onSelect'] = (
    e,
    _state,
    event
  ) => {
    // Keep the ⋯ menu open and open the Include/Exclude/AND sub-menu anchored to
    // the right of this row.
    if (e.key === Action.searchLink) {
      cascadePopover.onOpen(event?.currentTarget ?? null);
      return;
    }

    moreMenuPopover.onClose();

    if (e.key === Action.edit) editSearchState.onTrue();
    else if (e.key === Action.copyID) {
      clip.copy(item.id).then(() => {
        const key = Date.now().toString();

        notify.complete(
          'ID Copied',
          'The ID has been copied to your clipboard.',
          key,
          {
            icon: notificationIconMap.EXPORT,
          }
        );
      });
    } else if (e.key === Action.viewCampaign) {
      window.open(
        SMART_LEAD_CAMPAIGN_ANALYTICS_URL.replace(
          ':id',
          item.integrations.SMART_LEAD?.id ?? ''
        ),
        '_blank'
      );
    } else if (e.key === Action.retry) {
      if (
        item.integrations &&
        item.integrations.SMART_LEAD?.response?.leadLimitExhausted &&
        item.integrations.SMART_LEAD?.status === ExportIntegrationStatus.FAILED
      ) {
        integrationExportFailedDialogState.onTrue();
      } else if (
        item.integrations &&
        item.integrations.SMART_LEAD?.status === ExportIntegrationStatus.FAILED
      ) {
        retryIntegrationApi
          .mutateAsync({
            type: Object.keys(item.integrations)[0],
          })
          .then(() => {
            notify.progress(
              'Your export is in progress',
              `Adding ${item.records} Records...`,
              random(1, 101),
              { icon: notificationIconMap.EXPORT }
            );
          });
      }
    } else if (e.key === Action.downloadCSV) {
      if (
        item.integrations &&
        item.integrations.SMART_LEAD?.status === ExportIntegrationStatus.FAILED
      ) {
        downloadFile();
      }
    }
  };

  return (
    <>
      <Styled.Wrapper
        className={classnames('ExportTableAction', className)}
        state={moreMenuPopover}
        closeAfterSelect={false}
        icon="more-horizontal"
        variant="outlined"
        size="xsmall"
        popoverSx={SX.popover}
        contentSx={SX.content}
        itemSx={SX.item}
        itemDivider={true}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        items={getExportActionMenuItemsOptions(item)}
        onSelect={handleSelect}
      />

      <CascadeMenu
        state={cascadePopover}
        buttonElement={<span style={{ display: 'none' }} />}
        anchorOrigin={CASCADE_ANCHOR_ORIGIN}
        transformOrigin={CASCADE_TRANSFORM_ORIGIN}
        items={CASCADE_ITEMS}
        onSelect={handleCascadeSelect}
      />

      <EditExportDialog item={item} state={editSearchState} />
    </>
  );
});

ExportTableAction.displayName = 'ExportTableAction';
